import { ReactNode, useState, useEffect, useRef, useCallback } from 'react';
import Cookies from 'js-cookie';
import Head from 'next/head';
import Script from 'next/script';
import TagManager from 'react-gtm-module-defer';
import getConfig from 'next/config';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import axios from 'axios';
import {
  breadCrumbsData,
  addOrganizationJsonLd,
  addPageJsonLd,
  addHomeJsonLd,
  getStoreInfoBlockSchema,
  addBreadcrumbJsonLd,
  addLocaleToBreadCrumbs,
  addArticleJsonLd,
} from '@/utils/seo';
import useStore from '@/store/useStore';
import { getCountry } from '@/utils/i18n';
import { IS_DEV } from '@/utils/env';
import { REFER_URLS } from '@/utils/constants';
import { configAtatus } from '@/utils/logger';
import { getBrowserInfo } from '@/utils/utils';
import { getBuilderMessages } from '@/utils/builder';
import { trackEventWithObject } from '@/utils/gaDataPush';
import { setDynamicYieldRecommendationContext } from '@/utils/dynamicYield';
import { resolvePublishDate } from '@/utils/publishDate';
import useGlobalEData from '@/hooks/useGlobalEData';
import useMediaQuery from '@/hooks/useMediaQuery';
import useVisitedUrls from '@/hooks/useVisitedUrls';
import BackTop from '../BackTop/BackTop';

import RotatingPromotionBanner from '../BuilderIO/slimlinePromotionBanners/RotatingPromotionBanner';
import DigitalEngagementChat, { liveChatGAInit } from '../DigitalEngagementChat/DigitalEngagementChat';
import AppointmentBookingModal from '../AppointmentBookingModal/AppointmentBookingModal';

const { NEXT_PUBLIC_API_URL: DOMAIN_URL, NEXT_PUBLIC_APP_VER: APP_VER } = getConfig().publicRuntimeConfig;
const MobileFooter = dynamic(() => import('../Footer/MobileFooter'));
const Footer = dynamic(() => import('../Footer/Footer'));
const PromoModal = dynamic(() => import('../modals/PromoModal'), { ssr: false });
const GlobalEModal = dynamic(() => import('../modals/GlobalE'), { ssr: false });
const GoogleSignUpModal = dynamic(() => import('../modals/GoogleSignUpModal'), { ssr: false });
const SignInModal = dynamic(() => import('../modals/SignInModal'), { ssr: false });
const EmailUs = dynamic(() => import('../modals/EmailUs'), { ssr: false });

const BUYING_GUIDE_DEFAULT_OG_IMAGE =
  'https://cdn.builder.io/api/v1/image/assets%2F9f2a69003c86470ea05deb9ecb9887be%2F7cbc91f693724758a0ae52df451032bb';
const DEFAULT_OG_IMAGE = 'https://css.brilliantearth.com/static/img/email/NL-Drop-Hint---ZV-RAST-(2)_19.jpg';

export default function Layout({
  page = null,
  children,
  footerLinks,
  mobileFooterLinks,
  builderData,
}: {
  page?: any;
  children?: ReactNode;
  footerLinks?: any;
  mobileFooterLinks?: any;
  builderData?: any;
}) {
  const [hasInteraction, setHasInteraction] = useState(false);
  const [countryCode, setCountryCode] = useState('');
  const [countryCodeGlobalE, setCountryCodeGlobalE] = useState(null);
  const [userCountryReady, setUserCountryReady] = useState(false);
  const [geCountryReady, setGeCountryReady] = useState(false);
  const [enabledTopBanner, setEnabledTopBanner] = useState(false);
  const getHeaderItems = useStore(state => state.getHeaderItems);
  const isPromoModalVisible = useStore(state => state.isPromoModalVisible);
  const setIsPromoModalVisible = useStore(state => state.setIsPromoModalVisible);
  const loadUser = useStore(state => state.loadUser);
  const { currCountry, ...user } = useStore(state => state.user);
  const { store, telCode, country: showroomCountry } = useStore(state => state.showroom);
  const { localGeToggle } = useStore(state => state.glLocation);
  const country = getCountry().code;
  const phone = ['AU', 'CA'].includes(country) ? `${telCode}${store?.tel}` : store?.tel || '800.691.0952';
  const schemaCountry = currCountry || showroomCountry || 'United States';
  const router = useRouter();
  const { asPath, locale } = router;

  const isGlobalE = localGeToggle !== null && !localGeToggle;
  const isGlobalEModalVisible = useStore(state => state.isGlobalEModalVisible);
  const setIsGlobalEModalVisible = useStore(state => state.setIsGlobalEModalVisible);
  const isShowGoogleSignUpPopUp = useStore(state => state.isShowGoogleSignUpPopUp);
  const isShowSignInPopUp = useStore(state => state.isShowSignInPopUp);
  const isAppointmentBookingModalVisible = useStore(state => state.isAppointmentBookingModalVisible);
  const setIsAppointmentBookingModalVisible = useStore(state => state.setIsAppointmentBookingModalVisible);
  const setShowroomModalInfo = useStore(state => state.setAppointmentBookingInformation);
  const showroomModalInfo = useStore(state => state.appointmentBookingInformation);
  const emailUsModalVisible = useStore(state => state.emailUsModalVisible);
  const setEmailUsModalVisible = useStore(state => state.setEmailUsModalVisible);
  const isMobile = useMediaQuery('(max-width: 1023px)');
  const canonicalUrl = `${DOMAIN_URL}${locale && locale !== 'en-us' ? `/${locale}` : ''}${asPath}`;
  const dynamicRegionUrl = `${DOMAIN_URL}${locale && locale !== 'en-us' ? `/${locale}/` : '/'}`;
  const pageJsonLds = builderData?.[2] || [];

  const closeAppointmentModal = () => {
    const emptyInfo = {
      modalName: '',
      displayName1: '',
      displayName2: '',
      addresLine1: '',
      addressLine2: '',
      id: '',
      city: '',
      state: '',
      zipCode: '',
      setsterLocationId: '',
      timezoneName: '',
      timezoneAbreviation: '',
      timezoneId: '',
    };
    setShowroomModalInfo('setsterAvailabilityDateTimes', {});
    setShowroomModalInfo('prevSelectedShowroomStore', emptyInfo);
    setShowroomModalInfo('selectedShowroomStore', emptyInfo);
    setShowroomModalInfo('selectedServiceName', '');
    setShowroomModalInfo('selectedServiceId', '');
    setShowroomModalInfo('selectedServiceDateNightId', '');
    setShowroomModalInfo('selectedDate', null);
    setShowroomModalInfo('selectedTime', '');
    setShowroomModalInfo('selectedTimezone', '');
    setShowroomModalInfo('selectedTimezoneId', '0');
    setShowroomModalInfo('selectedTimezoneName', '');
    setShowroomModalInfo('setsterAvailabilityDateTimesLoading', true);
    setShowroomModalInfo('currentTimezone', '');
    setIsAppointmentBookingModalVisible(false);
    setIsAppointmentBookingModalVisible(false);
  };

  const getArticleInfo = (currentPage: any, blockName: string) => {
    let articleTitle = '';
    let image = '';
    let desktopImage = '';
    const blocks = currentPage?.data?.blocks || [];
    const heroBanner = blocks.find(block => block?.component?.name === blockName);
    if (heroBanner) {
      articleTitle = heroBanner?.component?.options?.title || articleTitle;
      image = heroBanner?.component?.options?.background?.mobile || '';
      desktopImage = heroBanner?.component?.options?.background?.desktop || '';
    }
    return { articleTitle, image, desktopImage };
  };

  const { topPurpleBar, rotatingPromos } = getBuilderMessages(builderData);

  const isHome = asPath === '/' || asPath.startsWith('/search/?');
  const isOrganizationPage = isHome || asPath === '/about/';

  const builderRef = useRef<any>(null);
  const onScroll = () => {
    if (!hasInteraction) {
      setHasInteraction(true);
      window.removeEventListener('scroll', onScroll);
    }
  };

  const topBarGaEvents = [
    {
      dataAttributes: [
        { dataAttributeName: 'category', dataAttributeValue: 'Top Bar' },
        { dataAttributeName: 'action', dataAttributeValue: 'Click' },
        { dataAttributeName: 'label', dataAttributeValue: 'Promotion Message' },
      ],
    },
  ];

  const visitedUrls = useVisitedUrls();

  useEffect(() => {
    setDynamicYieldRecommendationContext(asPath);
  }, [asPath]);

  const setChatFields = useCallback(() => {
    if (user) {
      const browserInfo = getBrowserInfo();
      if ((window as any).embeddedservice_bootstrap?.prechatAPI) {
        (window as any).embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields({ Web_Page: document.title });
        (window as any).embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields({
          Web_Page_URL: window.location.href,
        });
        (window as any).embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields({
          Previous_Web_Page_URL: document.referrer,
        });
        visitedUrls.forEach((url, index) => {
          const prechatField: { [key: string]: string } = {};
          const previous = `Previous_Web_Page_URL${index + 1}`;
          prechatField[previous] = url;
          (window as any).embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields(prechatField);
        });

        (window as any).embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields({
          City: user.customerCity || 'other',
        });
        (window as any).embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields({ Country: schemaCountry });
        (window as any).embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields({
          IP_Address: user.customerIp || '000.000.000.00',
        });
        (window as any).embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields({
          Operation_System: browserInfo.OperatingSystem,
        });
        (window as any).embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields({ Device: browserInfo.Device });
        (window as any).embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields({ Browser: browserInfo.Browser });
        (window as any).embeddedservice_bootstrap.prechatAPI.setVisiblePrechatFields({
          How_can_we_help: {
            value: 'Product_and_General_Quest',
            isEditableByEndUser: true,
          },
        });
      }
    }
  }, [user, schemaCountry]);

  useEffect(() => {
    window.addEventListener('scroll', onScroll, { passive: true });
    let geSettled = false;
    const markGeReady = () => {
      if (geSettled) return;
      geSettled = true;
      setGeCountryReady(true);
    };
    (window as any).GlobalECountryCode.then(code => {
      setCountryCodeGlobalE(code);
      setEnabledTopBanner(['US', 'CA'].includes(code));
      markGeReady();
    }).catch(markGeReady);
    // initials.js only resolves when GlobalE.Country appears — never rejects. Match the
    // dataLayer 5s race so Global-E mode cannot leave __BE_ONETRUST_EXPECTED__ stuck true.
    const geTimeout = window.setTimeout(() => {
      if (!(window as any).GlobalE?.Country) markGeReady();
    }, 5000);

    configAtatus();
    TagManager.initialize({ gtmId: 'GTM-TFWHSR' });
    return () => window.clearTimeout(geTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onEmbeddedMessagingReady = () => {
      setChatFields();
    };

    window.addEventListener('onEmbeddedMessagingReady', onEmbeddedMessagingReady);

    return () => {
      window.removeEventListener('onEmbeddedMessagingReady', onEmbeddedMessagingReady);
    };
  }, [setChatFields]);

  useEffect(() => {
    loadUser()
      .then(code => {
        setCountryCode(code || '');
      })
      .catch(() => {
        setCountryCode('');
      })
      .finally(() => {
        setUserCountryReady(true);
      });
    getHeaderItems();
  }, [getHeaderItems, loadUser]);

  // Publish whether OneTrust will mount once geo mode is known. Never treat "still loading"
  // as "no CMP", and never claim no-CMP if the CMP is already on the page.
  useEffect(() => {
    if (!userCountryReady) return;
    if (localGeToggle === null) return;

    const onetrustExpectedKey = '__BE_ONETRUST_EXPECTED__';
    const shouldMount = Boolean((countryCode && !isGlobalE) || countryCodeGlobalE);
    if (shouldMount) {
      (window as any)[onetrustExpectedKey] = true;
      window.dispatchEvent(new Event('BEOneTrustExpectationReady'));
      return;
    }
    // Global-E without a country yet: CMP may still mount — mark expected so timers cannot
    // treat an unset flag as "no CMP". Downgrade to false only after GE country settles.
    if (isGlobalE && !geCountryReady) {
      (window as any)[onetrustExpectedKey] = true;
      return;
    }

    const ot = (window as any).OneTrust;
    const present =
      typeof (window as any).OnetrustActiveGroups !== 'undefined' ||
      (ot && (typeof ot.GetDomainData === 'function' || ot.geolocationResponse)) ||
      !!document.querySelector('script[src*="cookielaw.org"], script[data-domain-script]');
    if (present) return;

    (window as any)[onetrustExpectedKey] = false;
    window.dispatchEvent(new Event('BEOneTrustExpectationReady'));
  }, [userCountryReady, geCountryReady, countryCode, countryCodeGlobalE, isGlobalE, localGeToggle]);

  useEffect(() => {
    const anchorId = window.location.hash.replace('#', '');
    (window as any).dataLayer.push({ event: 'bluecore-trigger' });

    // set scroll restoration to manual if there's an anchor in the url
    if ('scrollRestoration' in window.history && window.history.scrollRestoration !== 'manual' && anchorId) {
      window.history.scrollRestoration = 'manual';
    }

    // Set productGroupWithLogic & pageGroup GA events
    if (page?.data?.pageGroup) (window as any).dataLayer.push({ pageGroup: page?.data?.pageGroup });
    if (page?.data?.productGroupWithLogic)
      (window as any).dataLayer.push({ productGroupWithLogic: page?.data?.productGroupWithLogic });

    if (page?.data?.url === '/error-page/') {
      (window as any).dataLayer.push({ error404: true });
    }

    async function pushCountryCodeToDataLayer() {
      try {
        const res = await Promise.race([
          (window as any).GlobalECountryCode,
          new Promise<never>((_, reject) => {
            setTimeout(() => {
              reject(new Error('Not GlobalECountryCode present'));
            }, 5000);
          }),
        ]);

        (window as any).dataLayer.push({
          country_code: res,
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
      }
    }
    pushCountryCodeToDataLayer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleBluecoreEmail = async event => {
      try {
        const gaCookie = document.cookie.split(';').find(c => c?.trim()?.startsWith('_ga='));
        const gaClientID = gaCookie?.split('=')[1];
        const payload = {
          event: 'identify',
          properties: {
            distinct_id: gaClientID,
            token: 'brilliant_earth',
            client: APP_VER, // optional
            device: getBrowserInfo().Device, // optional
            customer: {
              email: event.detail?.customerData?.email,
            },
          },
        };

        const response = await axios.post('https://api.bluecore.app/api/track/mobile/v1', JSON.stringify(payload), {
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.status !== 200) {
          throw new Error(response.statusText);
        }
      } catch (e) {
        /* eslint no-console: ["error", { allow: ["warn", "error"] }] */
        console.error('BLUECORE API ERROR', e);
      }
    };

    document.addEventListener('bluecore:emailCaptured', handleBluecoreEmail);
    return () => document.removeEventListener('bluecore:emailCaptured', handleBluecoreEmail);
  }, []);

  useEffect(() => {
    if (builderRef.current) {
      let prevAnchorTop = -1;
      const onAnchorScroll = event => {
        event.preventDefault();
      };
      const scrollToAnchor = anchor => {
        const anchorTop = anchor.getBoundingClientRect().top + window.scrollY;
        if (anchorTop !== prevAnchorTop) {
          prevAnchorTop = anchorTop;
          window.scrollTo({
            top: window.innerWidth < 768 ? anchorTop - 46 : anchorTop, // 46 is the height of navbar on mobile
            behavior: 'smooth',
          });
          setTimeout(() => {
            scrollToAnchor(anchor);
          }, 200);
        } else {
          window.removeEventListener('scroll', onAnchorScroll);
        }
      };
      const observer = new MutationObserver(() => {
        const anchorId = window.location.hash.replace('#', '');
        const anchor = document.getElementById(anchorId);

        if (anchor) {
          observer.disconnect();
          window.addEventListener('scroll', onAnchorScroll, { passive: false });
          scrollToAnchor(anchor);
        }
      });

      observer.observe(builderRef.current, { childList: true, subtree: true });
    }
  }, [builderRef]);

  useEffect(() => {
    if (isGlobalE) {
      /*
       * Global-e ADA
       */
      const adaIntervalId = setInterval(() => {
        const anchor = document.querySelector('.ge-flag-show.ge-switcher-flag > a');
        if (anchor) {
          clearInterval(adaIntervalId);
          anchor?.setAttribute('role', 'button');
          anchor?.setAttribute('tabindex', '0');
        }
      }, 500);

      /*
       * Global-e GA4 events
       */
      const gbleIntervalId = setInterval(() => {
        const glbe = document.querySelectorAll('.ge-flag-show.ge-switcher-flag');
        if (glbe[0]) {
          clearInterval(gbleIntervalId);
          /*
           *  Add listener in both mobile and desktop buttons
           */
          glbe.forEach(countryBtn => {
            countryBtn.childNodes.forEach(child => {
              child.addEventListener('click', () => {
                trackEventWithObject({
                  event: isMobile ? 'top_navigation' : 'header',
                  general_event_category: 'Country Feature Flag',
                  general_event_section: 'Main Icon',
                  general_event_details: 'Undefined',
                  affiliation: currCountry === 'united states' ? 'domestic' : 'international',
                });
              });
            });
          });
          /*
           * Fired when glbe modal is closed
           */
          if (window['glegem']) {
            window['glegem']('OnShippingSwitcherClosed', result => {
              if (result.button === 'save') {
                const countries = document.querySelector('#gle_selectedCountry') as HTMLSelectElement;
                const selected = countries.options[countries.selectedIndex];

                Cookies.set('sel_country', selected?.value, { path: '/', secure: true });

                trackEventWithObject({
                  event: isMobile ? 'top_navigation' : 'header',
                  general_event_category: 'Country Feature Flag',
                  general_event_section: 'Country Select',
                  general_event_details: selected?.text || 'Undefined',
                  affiliation: currCountry === 'united states' ? 'domestic' : 'international',
                });
              } else {
                trackEventWithObject({
                  event: isMobile ? 'top_navigation' : 'header',
                  general_event_category: 'Country Feature Flag',
                  general_event_section: 'Country Select',
                  general_event_details: 'Close',
                  affiliation: currCountry === 'united states' ? 'domestic' : 'international',
                });
              }
            });
          }
        }
      }, 500);
    }
  }, [isGlobalE, currCountry]);

  useEffect(() => {
    if (isAppointmentBookingModalVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [isAppointmentBookingModalVisible]);

  const pageDataBreadcrumbSchema = page?.data?.breadcrumbSchema
    ? JSON.stringify(addLocaleToBreadCrumbs(page?.data?.breadcrumbSchema, locale))
    : null;
  let breadcrumbs = pageDataBreadcrumbSchema || breadCrumbsData(page?.data?.blocks, locale);
  if (isHome) {
    breadcrumbs = `{
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
              "item":"${DOMAIN_URL}${locale && locale !== 'en-us' ? `/${locale}` : ''}"
          }
        ]
      }`;
  }

  const storeInfoData = getStoreInfoBlockSchema(page?.data?.blocks, asPath);

  let title = page?.data?.title;
  if (typeof page?.data?.title === 'object') title = page?.data?.title?.Default;

  const isReferPage = REFER_URLS.includes(page?.data?.url);

  const notIndexed = page?.data?.addRobots;
  const pageGroup = page?.data?.pageGroup;
  const isBuyingGuidePage = pageGroup === 'Buying Guide';
  // Prefer page model lastUpdatedAt so URL migrations do not change Article schema date
  const { datePublishedIso: datePublished } = resolvePublishDate(page?.data?.lastUpdatedAt, page?.lastUpdated);
  let articleImage = '';
  let articleTitle = title;
  if (page?.data?.blocks) {
    const {
      articleTitle: slimHeroTitle,
      image: slimHeroImage,
      desktopImage: slimHeroDesktopImage,
    } = getArticleInfo(page, 'SlimHeroBanner');
    const {
      articleTitle: standardHeroTitle,
      image: standardHeroImage,
      desktopImage: standardHeroDesktopImage,
    } = getArticleInfo(page, 'StandardHeroBanner');
    articleTitle = standardHeroTitle || slimHeroTitle || articleTitle;
    articleImage =
      standardHeroImage || slimHeroImage || standardHeroDesktopImage || slimHeroDesktopImage || articleImage;
  }
  const ogImage =
    page?.data?.image || (isBuyingGuidePage ? articleImage || BUYING_GUIDE_DEFAULT_OG_IMAGE : DEFAULT_OG_IMAGE);

  useGlobalEData();

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={addPageJsonLd(title, canonicalUrl, page?.data?.description, locale)}
          key="webpage-jsonld"
        />
        <script
          dangerouslySetInnerHTML={addBreadcrumbJsonLd(breadcrumbs)}
          id="breadcrumb_schema"
          key="breadcrumb-jsonld"
          type="application/ld+json"
        />
        {pageJsonLds.map((jsonLd, index) => {
          if (jsonLd.pagelink !== page?.data?.url) return null;
          return (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: jsonLd.json }}
              key={`page-jsonld-${index}`}
            />
          );
        })}
        {isBuyingGuidePage && datePublished && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={addArticleJsonLd(
              canonicalUrl,
              articleTitle,
              page?.data?.description,
              datePublished,
              articleImage
            )}
            key="article-jsonld"
          />
        )}
        {isOrganizationPage && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={addOrganizationJsonLd(phone, schemaCountry, dynamicRegionUrl)}
            key="organization-jsonld"
          />
        )}
        {isHome && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={addHomeJsonLd(dynamicRegionUrl)}
            key="home-jsonld"
          />
        )}
        {storeInfoData && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: storeInfoData }}
            id="store-jsonld"
            key="store-jsonld"
          />
        )}
        {title && <title>{title}</title>}
        <meta charSet="utf-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="author" content="" />
        {page?.data?.keywords && <meta content={page?.data?.keywords} name="keywords" />}
        {page?.data?.description && <meta content={page?.data?.description} name="description" />}
        {notIndexed && <meta name="robots" content="noindex, nofollow" />}
        <meta name="verify-v1" content="TmCKnCEalT7vIakqjsybZcH2Mrg+aTLG6pq9W6uMo7w=" />
        {isMobile ? (
          <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no, user-scalable=no" />
        ) : (
          <meta name="viewport" content="width=device-width, initial-scale=1" />
        )}

        <meta property="og:title" content="Brilliant Earth" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.brilliantearth.com" />
        <meta property="og:site_name" content="Brilliant Earth" />
        <meta property="og:image" content={ogImage} />
        <meta property="fb:admins" content="2526520" />
        <meta name="google-site-verification" content="_dUvGH2ulARKrTxCxhSR1worprsBME7IubLlkwOCtYU" />
        <meta key="secondGV" name="google-site-verification" content="fjD2mwTgkChg9ACBGI4S_eG_0cU3c_CYUAeDYssLbqU" />
        <link rel="canonical" key="canonical" href={canonicalUrl} />

        {isReferPage && (
          <>
            <script
              async
              src="//d2jjzw81hqbuqv.cloudfront.net/integration/clients/brilliant-earth.min.js"
              type="text/javascript"
            />
            <script>
              {`
                window._talkableq = window._talkableq || [];
                window._talkableq.unshift(['init', { site_id: 'brilliant-earth' }]);

                  var email = '';
                  var first_name = '';
                  var last_name = '';

                window._talkableq.push(['authenticate_customer', {
                  email: email.replace('(at)', '@').replace('(dot)', '.'),
                  first_name: first_name,
                  last_name: last_name
                }]);
                window._talkableq.push(['register_affiliate', {}]);
              `}
            </script>
          </>
        )}
      </Head>
      {isGlobalE && (
        <>
          <script>
            {`
              function userLoc(userLocalization) {
                try {
                  var URL = document.location.href.toLowerCase();
                  if (URL.includes("/en-ca") && userLocalization.Country.Code != 'CA') {
                    GEM_Components.ExternalMethodsComponent.ShowShippingSwitcher('CA', 'CAD', 'en-GB', true, null);
                  } else if (URL.includes("/en-gb") && userLocalization.Country.Code != 'GB') {
                    GEM_Components.ExternalMethodsComponent.ShowShippingSwitcher('GB', 'GBP', 'en-GB', true, null);
                  } else if (URL.includes("/en-au") && userLocalization.Country.Code != 'AU') {
                    GEM_Components.ExternalMethodsComponent.ShowShippingSwitcher('AU', 'AUD', 'en-GB', true, null);
                  }
                } catch (e) {
                  if(geCustomLog != null){
                    console.log('External blocking mode exception');
                  }
                }
              };
              window["GlobalE_Loaded"] = function () {
                if (document.location.host == 'uat.brilliantearth.com') {
                  GEM_Components.ExternalMethodsComponent.GetUserLocalization(userLoc);
                }
              };
            `}
          </script>
          <Head>
            <link
              id="GEPIStyles"
              rel="stylesheet"
              href={`//${IS_DEV ? 'intgepi.bglobale' : 'gepi.global-e'}.com/includes/css/1467`}
            />
          </Head>
          <Script id="globale" strategy="afterInteractive">
            {`
              (function () {
                  var s = document.createElement('script');
                  s.type = 'text/javascript';
                  s.async = true;
                  s.src = '//${IS_DEV ? 'intgepi.bglobale' : 'gepi.global-e'}.com/includes/js/1467';
                  document.getElementsByTagName('head')[0].appendChild(s);
                  })();
                  `}
          </Script>
        </>
      )}
      {!enabledTopBanner && (isGlobalE === null || isGlobalE) && (
        <div id="GlobaleFreeShippingBannerContainer" className="tw-min-h-8 tw-bg-[#183E40]" />
      )}
      {(enabledTopBanner || (isGlobalE !== null && !isGlobalE)) && (
        <RotatingPromotionBanner
          backgroundColor={rotatingPromos?.options?.backgroundColor}
          fontColor={rotatingPromos?.options?.fontColor}
          gaEvents={topBarGaEvents}
          content={topPurpleBar}
          messages={rotatingPromos?.options?.messages}
          PromoMessagePosition={rotatingPromos?.options?.PromoMessagePosition}
        />
      )}
      <BackTop />
      <DigitalEngagementChat />
      <div ref={builderRef} className="tw-relative tw-min-h-screen">
        {children}
        {isReferPage && (
          <div
            className="tw-m-auto tw-mb-[50px] tw-pt-[70px] container container1260 min-[1290px]:!tw-w-auto min-[1290px]:!tw-max-w-[1290px] min-[1290px]:!tw-px-[15px]"
            id="talkable-offer"
          />
        )}
      </div>
      <MobileFooter links={mobileFooterLinks} className="tw-block md:tw-hidden" />
      <Footer links={footerLinks} className="tw-hidden md:tw-block" />

      {hasInteraction && (
        <Script
          strategy="lazyOnload"
          src="https://acsbapp.com/apps/app/assets/js/acsb.js"
          onReady={() => (window as any).initAccessibilityWidget()}
        />
      )}
      <Script
        strategy="afterInteractive"
        src={`${IS_DEV ? 'https://brilliantearth--uat.sandbox.my.site.com' : 'https://brilliantearth.my.site.com'}/ESWWebMessagingDeployme${IS_DEV ? '1784212864559' : '1718090818864'}/assets/js/bootstrap.min.js`}
        onReady={() => {
          (window as any).initEmbeddedMessaging();
          liveChatGAInit();
        }}
      />
      {((countryCode && isGlobalE !== null && !isGlobalE) || countryCodeGlobalE) && (
        <>
          <Script id="oneTrustConf" strategy="afterInteractive">
            {`
              var OneTrust = {
                geolocationResponse: {
                  countryCode: '${
                    // eslint-disable-next-line no-nested-ternary
                    isGlobalE ? countryCodeGlobalE : getCountry().code === 'GB' ? 'GB' : countryCode
                  }'
                }
              };
            `}
          </Script>
          <Script
            data-domain-script={`bed019e8-8146-42ad-9acc-2421b07b8b57${IS_DEV ? '-test' : ''}`}
            src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
            strategy="lazyOnload"
          />
        </>
      )}

      {isPromoModalVisible && (
        <>
          <PromoModal setIsModalVisible={setIsPromoModalVisible} />
          <div className="modal-backdrop fade in" onClick={() => setIsPromoModalVisible(false)} aria-hidden />
        </>
      )}

      {isGlobalEModalVisible && <GlobalEModal setIsModalVisible={setIsGlobalEModalVisible} />}
      {isShowGoogleSignUpPopUp && <GoogleSignUpModal />}
      {isShowSignInPopUp && <SignInModal />}
      {isAppointmentBookingModalVisible && (
        <>
          <AppointmentBookingModal user={user} onClose={closeAppointmentModal} />
          <div
            className="modal-backdrop fade in"
            role="button"
            tabIndex={0}
            onClick={() => {
              const activePageNames = {
                1: 'select a location',
                2: 'select a service',
                3: 'select date and time',
                4: 'confirmation',
              };

              (window as any).dataLayer.push({
                event: 'appointment_form',
                showroom_page_section: activePageNames[showroomModalInfo.activePage],
                showroom_action: 'close pop-up|outside close',
                showroom_location: showroomModalInfo.selectedShowroomStore.modalName,
                general_event_details: 'Undefined',
              });

              closeAppointmentModal();
            }}
          />
        </>
      )}
      {emailUsModalVisible && (
        <>
          <EmailUs setIsModalVisible={setEmailUsModalVisible} />
          <div className="modal-backdrop fade in" onClick={() => setEmailUsModalVisible(false)} aria-hidden />
        </>
      )}
    </>
  );
}
